/**
 * Design tokens extracted from Paper design file "CornerDesign Refactor"
 * Artboard: "2 — Main App (Empty State)" (1I-0)
 *
 * Structural tokens use CSS variables for automatic light/dark switching.
 * Brand/accent colors stay static — they're identity colors consistent across themes.
 */

export const APP = {
  // ─── Backgrounds ────────────────────────────────
  bgMain: 'var(--app-bg-main)',
  bgSidebar: 'var(--app-bg-sidebar)',
  bgContent: 'var(--app-bg-content)',
  bgSearchBar: 'var(--app-bg-search)',
  bgNavActive: 'var(--app-bg-nav-active)',
  bgHistoryActive: 'var(--app-bg-history-active)',
  bgDropZone: '#E4DDD433', // semi-transparent overlay — works on both themes
  bgQuickAction: 'var(--app-bg-quick-action)',
  bgCodeBlock: 'var(--app-bg-code-block)',
  bgSubmitBtn: 'var(--app-bg-submit)',
  /** Alias for `bgSubmitBtn` — some call sites still use the shorter name; keep both for stable builds. */
  bgSubmit: 'var(--app-bg-submit)',
  bgInputBar: 'var(--app-bg-input-bar)',
  bgCardBody: 'var(--app-bg-card-body)',
  bgWorkflowIcon: '#F0DDD5', // brand accent bg — stays warm
  bgFreeBadge: '#F0E8DF',
  bgProBadge: '#E8F0FE',
  bgEnterpriseBadge: '#F0E6FF',
  bgPdfBadge: '#E8534A',   // file type badge — brand color
  bgDocBadge: '#2563EB',   // file type badge — brand color
  bgImgBadge: '#0891B2',   // file type badge — brand color

  // ─── Borders ────────────────────────────────────
  border: 'var(--app-border)',
  borderCard: 'var(--app-border-card)',
  borderDropZone: 'var(--app-border-drop)',
  borderQuickAction: 'var(--app-border-quick-action)',
  borderFreeBadge: '#D9C4AE',
  borderProBadge: '#A8C4F0',
  borderEnterpriseBadge: '#C4A8F0',

  // ─── Text ───────────────────────────────────────
  textPrimary: 'var(--app-text-primary)',
  textSecondary: 'var(--app-text-secondary)',
  textMuted: 'var(--app-text-muted)',
  textDescription: 'var(--app-text-description)',
  textHistoryMuted: 'var(--app-text-history-muted)',
  textUpgrade: '#A08878',
  textUpgradeDecor: '#C4A88A',
  textFreeBadge: '#8B5E3C',
  textProBadge: '#1A56DB',
  textEnterpriseBadge: '#7C3AED',
  textGreen: '#22883F',
  textViewAll: '#C5BFB8',
  textDot: '#B8A898',
  textDark: 'var(--app-text-dark)',
  textCardBody: 'var(--app-text-card-body)',
  textCardMeta: 'var(--app-text-card-meta)',
  textCardDetail: 'var(--app-text-card-detail)',
  dotSeparator: 'var(--app-dot-separator)',

  // ─── Accent ─────────────────────────────────────
  greenDot: '#4DB96C',
  avatarGradient: 'linear-gradient(145deg, oklab(64.7% 0.065 0.084), oklab(39.4% 0.060 0.064))',
  avatarShadow: '#5A280A38 0px 1px 4px',
  inputShadow: '#1A17140F 0px 1px 4px',
  cornerGradientStart: '#D46A3A',
  cornerGradientEnd: '#8B3A10',

  // ─── Icon colors ────────────────────────────────
  iconDefault: 'var(--app-icon-default)',
  iconDots: 'var(--app-icon-dots)',

  // ─── Context menu / Recent panel ──────────────
  bgContextMenu: 'var(--app-bg-context-menu)',
  borderContextMenu: 'var(--app-border-context-menu)',
  shadowContextMenu: 'var(--app-shadow-context-menu)',
  bgMenuHover: 'var(--app-bg-menu-hover)',
  bgRecentPanel: 'var(--app-bg-recent-panel)',
  bgRecentCard: 'var(--app-bg-recent-card)',
  borderRecentCard: 'var(--app-border-recent-card)',
  shadowRecentCard: 'var(--app-shadow-recent-card)',
  borderRecentRow: 'var(--app-border-recent-row)',
  bgRowHover: 'var(--app-bg-row-hover)',
  bgDotsHover: 'var(--app-bg-dots-hover)',

  // ─── Typography ─────────────────────────────────
  fontFamily: '"Geist", system-ui, sans-serif',

  // ─── Sidebar dimensions ─────────────────────────
  sidebarWidth: 220,
  sidebarNavPadding: '12px',
  sidebarNavItemRadius: '7px',
  sidebarNavItemPadding: '7px 10px',
  sidebarNavItemGap: '8px',

  // ─── Top bar ────────────────────────────────────
  topBarHeight: 40,

  // ─── Input bar ──────────────────────────────────
  inputBarRadius: '12px',
  inputBarPadding: '10px 14px',

  // ─── Document viewer ──────────────────────────
  bgDocViewer: 'var(--app-bg-doc-viewer)',
  bgDocSheet: 'var(--app-bg-doc-sheet)',
  bgPaginationBar: 'var(--app-bg-pagination)',
  bgSuggestionBar: 'var(--app-bg-pagination)',
  docSheetShadow: '#0000001F 0px 4px 20px',
  borderPaginationDivider: 'var(--app-border-quick-action)',
  borderDocTopBar: 'var(--app-border-doc-top)',
  bgDocColumn: 'var(--app-bg-doc-column)',

  // ─── Chat panel ───────────────────────────────
  bgChatPanel: 'var(--app-bg-chat-panel)',
  bgCornerBubble: 'var(--app-bg-corner-bubble)',
  bgUserBubble: '#EDCFB8', // brand accent — warm tint
  textCornerLabel: 'var(--app-text-corner-label)',
  textReviewingSub: 'var(--app-text-reviewing-sub)',
  bgChatSendBtn: '#C4622A', // brand accent
  bgChatInput: 'var(--app-bg-chat-input)',
  bgFileBadge: '#D97645', // brand accent
  textFileBadgeDismiss: '#C4A99A',
  textFileChipName: 'var(--app-text-dark)',
  bgInitialChip: 'var(--app-bg-initial-chip)',
  bgFollowUpChip: 'var(--app-bg-followup-chip)',
  textUserBubble: 'var(--app-text-user-bubble)',
  bgChatInputActive: 'var(--app-bg-chat-input-active)',
  borderChatInputAccent: '#D46A3A', // brand accent
  chipBadgePdf: '#D97645',
  chipBadgeMp3: '#8B7355',
  chipBadgeDoc: '#6B8CA8',
  chipBadgeImg: '#7A9E7E',
  bgQueueIcon: '#FDF0EA',
  textQueueBody: '#9B8E83',
  textNewConversation: 'var(--app-text-dark)',

  // ─── Version timeline ────────────────────────
  bgVersionCanvas: 'var(--app-bg-version-canvas)',
  versionOriginal: '#D46A3A', // brand accent
  versionSigned: '#2D6E5A',
  versionCurrent: 'var(--app-text-primary)',
  versionCurrentDot: '#4ADE80',
  versionChanged: '#FDE68A',
  versionLegendOriginal: '#8B7355',
  versionArrow: '#C8C0B8',
  bgVersionCard: 'var(--app-bg-version-card)',

  // ─── Audio player ──────────────────────────────
  bgAudioIcon: '#2B2420',
  bgPlayBtn: '#2B2420',
  waveformPlayed: '#D46A3A', // brand accent
  waveformUnplayed: '#D4CCC3',
  playhead: '#D46A3A',
  playheadRing: '#FFFFFF',
  textTimestamp: 'var(--app-text-secondary)',
  textAudioTitle: 'var(--app-text-dark)',
  textAudioMeta: 'var(--app-text-secondary)',
  bgSpeedBtn: 'var(--app-bg-speed-btn)',
  borderSpeedBtn: 'var(--app-border-speed-btn)',
  textTranscriptLabel: 'var(--app-text-dark)',
  textViewFull: '#D46A3A', // brand accent
  textTranscriptTime: '#D46A3A',
  textTranscriptBody: 'var(--app-text-dark)',
  bgAudioActionBar: 'var(--app-bg-audio-action)',
  textAudioChip: 'var(--app-text-card-detail)',
  textAudioChipAccent: '#D46A3A',
  bgAudioChip: 'var(--app-bg-audio-chip)',
  borderAudioChip: 'var(--app-border-audio-chip)',

  // ─── Chat transcription card ─────────────────
  bgTranscriptCardHeader: 'var(--app-bg-transcript-header)',
  bgTranscriptCardBody: 'var(--app-bg-transcript-body)',
  borderTranscriptCard: 'var(--app-border-transcript)',
  textTranscribedMeta: 'var(--app-text-card-detail)',
  bgDownloadBtn: 'var(--app-bg-download-btn)',
  borderDownloadBtn: 'var(--app-border-download-btn)',
  textDownloadBtn: 'var(--app-text-card-detail)',

  // ─── Image viewer ─────────────────────────────
  textImageLabel: '#9B8E83',
  bgImageCard: '#D9D3CB',
  bgImageBadge: '#00000066',
  textImageBadge: '#FFFFFFCC',
  textImageCaption: '#9B8E83',
  textImageArrowLabel: '#D46A3A',
  bgResultCheckerboard: '#E4DDD4',
  bgImageResultAccentBtn: '#FDF0EA',
  borderImageResultAccentBtn: '#D46A3A',
  textImageResultAccent: '#D46A3A',
  bgImageResultBtn: 'var(--app-bg-speed-btn)',
  borderImageResultBtn: 'var(--app-border-speed-btn)',
  textImageResultBtn: 'var(--app-text-card-detail)',
  textImageResultCaption: '#9B8E83',

  // ─── Tool panel ───────────────────────────────
  bgToolPanel: 'var(--app-bg-tool-panel)',
  borderToolPanel: 'var(--app-border-tool-panel)',
  bgToolActive: 'var(--app-bg-tool-active)',
  strokeToolActive: '#D46A3A', // brand accent
  strokeToolInactive: 'var(--app-icon-default)',

  // ─── Expanded tool panel ────────────────────────
  bgToolSearch: 'var(--app-bg-tool-search)',
  bgToolRowExpanded: 'var(--app-bg-tool-search)',
  bgToolSettingsPanel: 'var(--app-bg-tool-settings)',
  borderToolSettings: 'var(--app-border-tool-settings)',
  textToolCategory: '#9B9390',
  textToolSearchPlaceholder: '#9B9390',

  // ─── Export bar ─────────────────────────────────
  bgExportBar: 'var(--app-bg-export-bar)',
  bgExportChip: 'var(--app-bg-export-chip)',
  bgExportChipActive: '#D97645', // brand accent
  borderExportChip: 'var(--app-border-export-chip)',
  textExportLabel: 'var(--app-text-muted)',
  bgExportAction: '#D97645', // brand accent

  // ─── Workflow builder ───────────────────────────
  bgWorkflowCanvas: 'var(--app-bg-workflow-canvas)',
  bgWorkflowProperties: 'var(--app-bg-workflow-properties)',
  borderWorkflowNode: 'var(--app-border-workflow-node)',
  borderWorkflowNodeSelected: '#C4622A', // brand accent
  bgWorkflowNode: 'var(--app-bg-workflow-node)',
  bgWorkflowBranchYes: '#F3F7F1',
  bgWorkflowBranchNo: '#F8F3F1',
  textWorkflowNodeLabel: 'var(--app-text-primary)',
  textWorkflowNodeMeta: 'var(--app-text-description)',
  accentWorkflowPrimary: '#D97645', // brand accent
  accentWorkflowSuccess: '#1B7A4B',
  bgWorkflowConnector: '#D4CFC7',
  bgWorkflowTopBar: 'var(--app-bg-workflow-top-bar)',
  borderWorkflowTopBar: 'var(--app-border-workflow-top-bar)',
  bgWorkflowTemplateBanner: 'var(--app-bg-workflow-template)',

  // ─── Review mode ──────────────────────────────
  bgReviewBar: '#FBF0E8',
  borderReviewBar: '#F0DDD5',
  bgReviewCardActive: '#FFF8F0',
  borderReviewCardActive: '#D97645', // brand accent
  bgReviewCardDefault: 'var(--app-bg-review-card-default)',
  borderReviewCardDefault: 'var(--app-border-review-card-default)',
  bgReviewCardAccepted: '#F2F8F4',
  borderReviewCardAccepted: '#D5E3D9',
  textReviewAccepted: '#6B8F71',
  bgReviewCardDismissed: '#F8F6F3',
  borderReviewCardDismissed: '#EDE8E2',
  textReviewDismissed: 'var(--app-text-muted)',
  bgReviewHighlight: '#FFF8F0',
  borderReviewHighlight: '#F0DDD5',
  bgReviewHighlightActive: '#FBF0E8',
  borderReviewHighlightActive: '#D97645',
  bgReviewProgress: 'var(--app-bg-review-progress)',
  bgReviewCategoryPill: 'var(--app-bg-review-category)',
  bgReviewTag: 'var(--app-bg-review-tag)',
  bgReviewPriorityHigh: '#F0DDD5',
  textReviewPriorityHigh: '#D97645',
  textReviewMuted: 'var(--app-text-muted)',
  textReviewProposed: '#6B8F71',
  textReviewInfo: 'var(--app-text-card-detail)',
} as const;

/** Return the chip badge color for file chips in the chat input area (muted earthy tones). */
export function getFileChipBadgeColor(type?: string | null): string {
  const t = (type || 'FILE').toUpperCase();
  if (t === 'DOC' || t === 'DOCX') return APP.chipBadgeDoc;
  if (t === 'IMG' || t === 'PNG' || t === 'JPG' || t === 'JPEG') return APP.chipBadgeImg;
  if (t === 'MP3' || t === 'WAV' || t === 'OGG' || t === 'M4A') return APP.chipBadgeMp3;
  return APP.chipBadgePdf;
}

/** Return the badge background color for sidebar history badges (bright saturated). */
export function getFileBadgeColor(type?: string | null): string {
  const t = (type || 'FILE').toUpperCase();
  if (t === 'DOC' || t === 'DOCX') return APP.bgDocBadge;
  if (t === 'IMG' || t === 'PNG' || t === 'JPG' || t === 'JPEG') return APP.bgImgBadge;
  return APP.bgPdfBadge;
}
